import React, { useEffect, useState } from 'react';
import { 
  Calendar, 
  Send,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  Sparkles,
  ListPlus,
  BookOpen
} from 'lucide-react';
import { promptsApi, seedApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';

const Prompts = () => {
  const [prompts, setPrompts] = useState([]);
  const [myResponses, setMyResponses] = useState([]);
  const [currentPromptIndex, setCurrentPromptIndex] = useState(0);
  const [response, setResponse] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [creatingTask, setCreatingTask] = useState(null);

  useEffect(() => {
    loadPrompts();
  }, []);

  const loadPrompts = async () => {
    try {
      await seedApi.seed().catch(() => {});
      const [promptsRes, responsesRes] = await Promise.all([
        promptsApi.getAll(),
        promptsApi.getMyResponses().catch(() => ({ data: [] })),
      ]);
      setPrompts(promptsRes.data || []);
      setMyResponses(responsesRes.data || []);
      
      // Set current prompt to today's day of week
      const today = new Date().getDay();
      const todayIndex = (promptsRes.data || []).findIndex(p => p.day_of_week === (today === 0 ? 6 : today - 1));
      if (todayIndex !== -1) {
        setCurrentPromptIndex(todayIndex);
      }
    } catch (error) {
      console.error('Failed to load prompts:', error);
      toast.error('Failed to load prompts');
    } finally {
      setLoading(false);
    }
  };

  const currentPrompt = prompts[currentPromptIndex];
  
  const hasResponded = (promptId) => {
    return myResponses.some(r => r.prompt_id === promptId);
  };

  const getResponseForPrompt = (promptId) => {
    return myResponses.find(r => r.prompt_id === promptId);
  };

  const handleSubmitResponse = async () => {
    if (!response.trim() || !currentPrompt) return;
    
    setSubmitting(true);
    try {
      const result = await promptsApi.respond(currentPrompt.id, response);
      setMyResponses(prev => [...prev, {
        id: result.data.id,
        prompt_id: currentPrompt.id,
        response_text: response,
        created_at: new Date().toISOString()
      }]);
      setResponse('');
      toast.success('Response saved!');
    } catch (error) {
      toast.error('Failed to save response');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateTask = async (responseId) => {
    setCreatingTask(responseId);
    try {
      await promptsApi.createTaskFromResponse(responseId);
      // Update local state to mark task as created
      setMyResponses(prev => prev.map(r => 
        r.id === responseId ? { ...r, task_created: true } : r
      ));
      toast.success('Task created from your reflection! Check your Tasks page.');
    } catch (error) {
      toast.error('Failed to create task');
    } finally {
      setCreatingTask(null);
    }
  };

  const getDayName = (dayOfWeek) => {
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    return days[dayOfWeek] || 'Unknown';
  };

  const getCategoryColor = (category) => {
    const colors = {
      planning: 'bg-[#26C6DA]/20 text-[#26C6DA]',
      strategy: 'bg-[#8B5CF6]/20 text-[#8B5CF6]',
      'problem-solving': 'bg-amber-500/20 text-amber-400',
      optimization: 'bg-emerald-500/20 text-emerald-400',
      innovation: 'bg-purple-500/20 text-purple-400',
      reflection: 'bg-blue-500/20 text-blue-400',
      visualization: 'bg-pink-500/20 text-pink-400',
    };
    return colors[category] || 'bg-slate-500/20 text-slate-500 dark:text-slate-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-slate-200 dark:border-[#1F2937] border-t-[#26C6DA] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in-up" data-testid="prompts-page">
      <div>
        <h1 className="text-3xl md:text-4xl font-bold text-slate-800 dark:text-slate-100" style={{ fontFamily: 'Outfit' }}>
          Daily Prompts
        </h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">
          Build strategic thinking habits with daily reflection exercises.
        </p>
      </div>

      {/* Progress Indicators */}
      <div className="flex items-center justify-center gap-2 py-4">
        {prompts.map((prompt, index) => (
          <button
            key={prompt.id}
            onClick={() => setCurrentPromptIndex(index)}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
              index === currentPromptIndex
                ? 'bg-gradient-to-r from-[#26C6DA] to-[#8B5CF6] text-black scale-110'
                : hasResponded(prompt.id)
                ? 'bg-emerald-500/20 text-emerald-400'
                : 'bg-[#1F2937] text-slate-500 dark:text-slate-400 hover:bg-[#26C6DA]/20'
            }`}
            data-testid={`prompt-day-${index}`}
          >
            {hasResponded(prompt.id) ? (
              <CheckCircle className="w-5 h-5" />
            ) : (
              <span className="text-sm font-medium">{getDayName(prompt.day_of_week)?.charAt(0)}</span>
            )}
          </button>
        ))}
      </div>

      {/* Current Prompt Card */}
      {currentPrompt && (
        <div className="bg-gradient-to-br from-[#8B5CF6] to-[#26C6DA] rounded-2xl p-8 text-white relative overflow-hidden" data-testid="current-prompt-card">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
          
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-white/80" />
                <span className="text-white/80 font-medium">
                  {getDayName(currentPrompt.day_of_week)}
                </span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium bg-white/20 text-white`}>
                  {currentPrompt.category}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPromptIndex(prev => Math.max(0, prev - 1))}
                  disabled={currentPromptIndex === 0}
                  className="p-2 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-50 transition-colors"
                  data-testid="prev-prompt-btn"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setCurrentPromptIndex(prev => Math.min(prompts.length - 1, prev + 1))}
                  disabled={currentPromptIndex === prompts.length - 1}
                  className="p-2 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-50 transition-colors"
                  data-testid="next-prompt-btn"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>

            <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Outfit' }}>
              {currentPrompt.title}
            </h2>
            
            {/* Scripture Reference Badge */}
            {currentPrompt.scripture_reference && (
              <div className="flex items-center gap-2 mb-4 bg-white/10 rounded-lg px-3 py-2 w-fit">
                <BookOpen className="w-4 h-4" />
                <span className="text-sm font-medium">{currentPrompt.scripture_reference}</span>
              </div>
            )}
            
            <p className="text-xl text-white/90 leading-relaxed whitespace-pre-line">
              {currentPrompt.prompt_text}
            </p>
          </div>
        </div>
      )}

      {/* Response Section */}
      {currentPrompt && (
        <div className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-2xl p-6" data-testid="response-section">
          {hasResponded(currentPrompt.id) ? (
            <div>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 text-emerald-400">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">You've responded to this prompt</span>
                </div>
                {/* Add as Task Button */}
                {!getResponseForPrompt(currentPrompt.id)?.task_created && (
                  <Button
                    onClick={() => handleCreateTask(getResponseForPrompt(currentPrompt.id)?.id)}
                    disabled={creatingTask === getResponseForPrompt(currentPrompt.id)?.id}
                    variant="outline"
                    size="sm"
                    className="border-[#26C6DA]/50 text-[#26C6DA] hover:bg-[#26C6DA]/10"
                    data-testid="create-task-btn"
                  >
                    <ListPlus className="w-4 h-4 mr-2" />
                    {creatingTask === getResponseForPrompt(currentPrompt.id)?.id ? 'Creating...' : 'Add as Task'}
                  </Button>
                )}
                {getResponseForPrompt(currentPrompt.id)?.task_created && (
                  <span className="text-sm text-slate-500 flex items-center gap-1">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    Task created
                  </span>
                )}
              </div>
              <div className="bg-slate-100 dark:bg-[#1A1A2E] rounded-xl p-4 border border-slate-200 dark:border-[#1F2937]">
                <p className="text-slate-800 dark:text-slate-100 whitespace-pre-wrap">
                  {getResponseForPrompt(currentPrompt.id)?.response_text}
                </p>
                <p className="text-xs text-slate-500 mt-3">
                  {formatDate(getResponseForPrompt(currentPrompt.id)?.created_at)}
                </p>
              </div>
              <p className="text-sm text-slate-500 mt-3 flex items-center gap-2">
                <ListPlus className="w-4 h-4" />
                If your reflection includes an action item, click "Add as Task" to turn it into a task.
              </p>
            </div>
          ) : (
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-[#26C6DA]" />
                <span className="font-medium text-slate-800 dark:text-slate-100">Your Response</span>
              </div>
              <Textarea
                value={response}
                onChange={(e) => setResponse(e.target.value)}
                placeholder="Take a moment to reflect and share your thoughts..."
                className="min-h-[150px] bg-slate-100 dark:bg-[#1A1A2E] border-slate-200 dark:border-[#1F2937] text-slate-800 dark:text-slate-100 placeholder:text-slate-500 focus:border-[#26C6DA] focus:ring-1 focus:ring-[#26C6DA] rounded-xl resize-none"
                data-testid="response-textarea"
              />
              <div className="flex justify-end mt-4">
                <Button
                  onClick={handleSubmitResponse}
                  disabled={!response.trim() || submitting}
                  className="bg-gradient-to-r from-[#26C6DA] to-[#8B5CF6] hover:from-[#0891B2] hover:to-[#7C3AED] text-black font-medium rounded-full px-6 gap-2"
                  data-testid="submit-response-btn"
                >
                  {submitting ? (
                    <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Submit Response
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Response History */}
      {myResponses.length > 0 && (
        <div data-testid="response-history">
          <h3 className="text-xl font-semibold text-slate-800 dark:text-slate-100 mb-4" style={{ fontFamily: 'Outfit' }}>
            Your Reflection History
          </h3>
          <div className="space-y-4">
            {myResponses.slice().reverse().slice(0, 5).map((resp, index) => {
              const prompt = prompts.find(p => p.id === resp.prompt_id);
              return (
                <div key={resp.id} className="bg-white dark:bg-[#111827] backdrop-blur-md border border-slate-200 dark:border-[#1F2937] rounded-xl p-5">
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-medium text-[#26C6DA]">{prompt?.title || 'Unknown Prompt'}</span>
                    <span className="text-xs text-slate-500">{formatDate(resp.created_at)}</span>
                  </div>
                  <p className="text-slate-600 dark:text-slate-300 line-clamp-3">{resp.response_text}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Prompts;
